import React, { useCallback, useEffect, useRef, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import useActions, { useAppSelector } from "../../../app/hooks";
import { selectFactotumStateTies, selectFactotumStates } from "../../library/librarySlice";
import { Factotum } from "../../../types/Factotum";
import { FactotumState } from "../../../types/FactotumState";
import { FactotumStateTie } from "../../../types/FactotumStateTie";

const SHEET_MIN_SIZE = 800;

function getStateById(states: FactotumState[], id: number): FactotumState | undefined {
  return states.find((s) => s.id === id);
}

export default function FactotumStateModal(props: {
  open: boolean;
  factotum: Factotum | null;
  onClose: () => void;
}) {
  const actions = useActions();
  const states = useAppSelector(selectFactotumStates);
  const stateTies = useAppSelector(selectFactotumStateTies);
  const sheetRef = useRef<HTMLDivElement>(null);

  const [dragFromState, setDragFromState] = useState<FactotumState | null>(null);
  const [dragEnd, setDragEnd] = useState<{ x: number; y: number } | null>(null);
  const [dragMoveState, setDragMoveState] = useState<{
    stateId: number;
    offsetX: number;
    offsetY: number;
  } | null>(null);
  const [dragMovePos, setDragMovePos] = useState<{ x: number; y: number } | null>(
    null
  );

  useEffect(() => {
    if (props.open && props.factotum) {
      actions.getFactotumStates(props.factotum.id);
      actions.getFactotumStateTies(props.factotum.id);
    }
  }, [props.open, props.factotum?.id]);

  const handleSheetDoubleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!props.factotum || !sheetRef.current) return;
      if ((e.target as HTMLElement).closest("[data-state-id]")) return;
      const rect = sheetRef.current.getBoundingClientRect();
      const pos_x = Math.round(e.clientX - rect.left);
      const pos_y = Math.round(e.clientY - rect.top);
      const name = window.prompt("State name?");
      if (name === null || name.trim() === "") return;
      actions.createFactotumState({
        factotumId: props.factotum.id,
        factotumStateCreateRequest: {
          pos_x,
          pos_y,
          name: name.trim(),
        },
      });
    },
    [props.factotum, actions]
  );

  const handleStateMouseDown = useCallback(
    (e: React.MouseEvent, state: FactotumState) => {
      if (e.shiftKey) {
        e.preventDefault();
        e.stopPropagation();
        setDragFromState(state);
        if (sheetRef.current) {
          setDragEnd({ x: state.pos_x, y: state.pos_y });
        }
      } else if (e.button === 0) {
        e.preventDefault();
        e.stopPropagation();
        if (!sheetRef.current) return;
        const rect = sheetRef.current.getBoundingClientRect();
        const offsetX = e.clientX - rect.left - state.pos_x;
        const offsetY = e.clientY - rect.top - state.pos_y;
        setDragMoveState({ stateId: state.id, offsetX, offsetY });
        setDragMovePos({ x: state.pos_x, y: state.pos_y });
      }
    },
    []
  );

  useEffect(() => {
    if (!dragFromState) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!sheetRef.current) return;
      const rect = sheetRef.current.getBoundingClientRect();
      setDragEnd({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
    };

    const handleMouseUp = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const stateEl = target.closest("[data-state-id]");
      const endStateId = stateEl ? parseInt(stateEl.getAttribute("data-state-id") ?? "", 10) : null;

      if (
        dragFromState &&
        endStateId != null &&
        !Number.isNaN(endStateId) &&
        endStateId !== dragFromState.id &&
        props.factotum
      ) {
        const raw = window.prompt("Likelihood (number)?", "1");
        if (raw !== null) {
          const likelihood = parseFloat(raw);
          if (!Number.isNaN(likelihood)) {
            actions.createFactotumStateTie({
              factotumId: props.factotum.id,
              factotumStateTieCreateRequest: {
                start_state_id: dragFromState.id,
                end_state_id: endStateId,
                likelihood,
              },
            });
          }
        }
      }

      setDragFromState(null);
      setDragEnd(null);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [dragFromState, props.factotum, actions]);

  useEffect(() => {
    if (!dragMoveState) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!sheetRef.current) return;
      const rect = sheetRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left - dragMoveState.offsetX;
      const y = e.clientY - rect.top - dragMoveState.offsetY;
      setDragMovePos({ x, y });
    };

    const handleMouseUp = (e: MouseEvent) => {
      if (!sheetRef.current) {
        setDragMoveState(null);
        setDragMovePos(null);
        return;
      }
      const rect = sheetRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left - dragMoveState.offsetX;
      const y = e.clientY - rect.top - dragMoveState.offsetY;
      const snappedX = Math.round(x);
      const snappedY = Math.round(y);
      const state = getStateById(states, dragMoveState.stateId);
      if (props.factotum && state) {
        actions.updateFactotumState({
          factotumId: props.factotum.id,
          stateId: state.id,
          factotumStateUpdateRequest: {
            name: state.name,
            pos_x: snappedX,
            pos_y: snappedY,
          },
        });
      }

      setDragMoveState(null);
      setDragMovePos(null);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [dragMoveState, states, actions, props.factotum]);

  if (!props.factotum) return null;

  const sheetWidth =
    Math.max(SHEET_MIN_SIZE, ...states.map((s) => s.pos_x), 0) + 120;
  const sheetHeight =
    Math.max(SHEET_MIN_SIZE, ...states.map((s) => s.pos_y), 0) + 120;

  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          minWidth: "90vw",
          minHeight: "85vh",
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle>State: {props.factotum.name}</DialogTitle>
      <DialogContent sx={{ overflow: "hidden", display: "flex", flexDirection: "column", p: 0 }}>
        <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1 }}>
          Double-click on the sheet to place a new state. Shift+drag from one state to another to
          connect them with a state tie (you will be prompted for likelihood).
        </Typography>
        <Box
          ref={sheetRef}
          onDoubleClick={handleSheetDoubleClick}
          sx={{
            flex: 1,
            minHeight: SHEET_MIN_SIZE,
            minWidth: SHEET_MIN_SIZE,
            position: "relative",
            bgcolor: "grey.100",
            border: "1px solid",
            borderColor: "divider",
            cursor: dragFromState ? "crosshair" : "default",
            overflow: "auto",
          }}
        >
          <Box
            sx={{
              position: "relative",
              width: sheetWidth,
              height: sheetHeight,
              minWidth: sheetWidth,
              minHeight: sheetHeight,
            }}
          >
          <Box
            component="svg"
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              pointerEvents: "none",
            }}
            width={sheetWidth}
            height={sheetHeight}
          >
            {stateTies.map((tie: FactotumStateTie) => {
              const start = getStateById(states, tie.start_state_id);
              const end = getStateById(states, tie.end_state_id);
              if (!start || !end) return null;

              const midX = (start.pos_x + end.pos_x) / 2;
              const midY = (start.pos_y + end.pos_y) / 2;
              const dx = end.pos_x - start.pos_x;
              const dy = end.pos_y - start.pos_y;
              const angle = (Math.atan2(dy, dx) * 180) / Math.PI;
              const len = Math.sqrt(dx * dx + dy * dy) || 1;
              const nx = -dy / len;
              const ny = dx / len;
              const labelOffset = 26;
              const labelX = midX + nx * labelOffset;
              const labelY = midY + ny * labelOffset;

              return (
                <g key={tie.id}>
                  <line
                    x1={start.pos_x}
                    y1={start.pos_y}
                    x2={end.pos_x}
                    y2={end.pos_y}
                    stroke="#000000"
                    strokeWidth={2}
                  />
                  <polygon
                    points="0,-6 10,0 0,6"
                    fill="#000000"
                    transform={`translate(${midX},${midY}) rotate(${angle})`}
                  />
                  <text
                    x={labelX}
                    y={labelY}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fontSize="10"
                    fill="#000000"
                    stroke="#ffffff"
                    strokeWidth={0.75}
                  >
                    {tie.likelihood}
                  </text>
                </g>
              );
            })}
            {dragFromState && dragEnd && (() => {
              const midX = (dragFromState.pos_x + dragEnd.x) / 2;
              const midY = (dragFromState.pos_y + dragEnd.y) / 2;
              const angle =
                (Math.atan2(dragEnd.y - dragFromState.pos_y, dragEnd.x - dragFromState.pos_x) *
                  180) /
                Math.PI;

              return (
                <g>
              <line
                x1={dragFromState.pos_x}
                y1={dragFromState.pos_y}
                x2={dragEnd.x}
                y2={dragEnd.y}
                stroke="#1976d2"
                strokeWidth={2}
                strokeDasharray="4 2"
              />
                  <polygon
                    points="0,-6 10,0 0,6"
                    fill="#1976d2"
                    transform={`translate(${midX},${midY}) rotate(${angle})`}
                  />
                </g>
              );
            })()}
          </Box>

          {states.map((state: FactotumState) => (
            <Box
              key={state.id}
              data-state-id={state.id}
              onMouseDown={(e) => handleStateMouseDown(e, state)}
              sx={{
                position: "absolute",
                left:
                  dragMovePos && dragMoveState?.stateId === state.id
                    ? dragMovePos.x
                    : state.pos_x,
                top:
                  dragMovePos && dragMoveState?.stateId === state.id
                    ? dragMovePos.y
                    : state.pos_y,
                transform: "translate(-50%, -50%)",
                minWidth: 80,
                py: 0.5,
                px: 1.5,
                bgcolor: "primary.main",
                color: "primary.contrastText",
                borderRadius: 1,
                typography: "body2",
                fontWeight: 500,
                boxShadow: 1,
                pointerEvents: "auto",
                cursor:
                  dragFromState || dragMoveState
                    ? "crosshair"
                    : "grab",
                userSelect: "none",
              }}
            >
              {state.name}
            </Box>
          ))}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
        <Button onClick={props.onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
